import { ThemeBus } from './events-bus.js';
import { EVENTS } from './event-constants.js';
import { Component } from './component-base.js';

/**
 * ModalComponent
 * 
 * A reusable modal component that can be used anywhere in the theme.
 * Supports multiple instances on a single page, each with a unique ID.
 * 
 * Features:
 * - Integrates with site-wide overlay (drawer-overlay system)
 * - Can be triggered via data attributes or ThemeBus events
 * - Supports multiple modals on the same page
 * - Closes on overlay click or close button
 * - Prevents body scroll when open
 * - Uses ThemeBus for event communication
 * 
 * Usage:
 * 
 * HTML Structure:
 * <modal-component data-modal-id="unique-modal-id">
 *   <div data-modal-dialog>
 *     <button data-modal-close>Close</button>
 *     <!-- Modal content here -->
 *   </div>
 * </modal-component>
 * 
 * Trigger button (anywhere on page):
 * <button data-modal-toggle="unique-modal-id">Open Modal</button>
 * 
 * Or via JavaScript:
 * ThemeBus.dispatchEvent(new CustomEvent(EVENTS.modalOpen, { detail: { id: 'unique-modal-id' } }));
 * ThemeBus.dispatchEvent(new CustomEvent(EVENTS.modalClose, { detail: { id: 'unique-modal-id' } }));
 * ThemeBus.dispatchEvent(new CustomEvent(EVENTS.modalToggle, { detail: { id: 'unique-modal-id' } }));
 */
class ModalComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.initialized) return;
    this.initialized = true;

    // Get the unique modal ID from data attribute
    this.modalId = this.dataset.modalId;
    if (!this.modalId) {
      console.warn('[Modal] Modal component missing data-modal-id attribute', this);
      return;
    }

    // Get references to key elements
    this.overlay = document.querySelector('[data-site-overlay]');
    this.dialog = this.querySelector('[data-modal-dialog]');
    this.closeButtons = this.querySelectorAll('[data-modal-close]');
    
    // Find all trigger buttons for this specific modal
    this.triggerButtons = document.querySelectorAll(`[data-modal-toggle="${this.modalId}"]`);

    // Track open state
    this.isOpen = false;

    // Setup event listeners
    this.#addEventListeners();

    // Ensure modal starts closed
    this.#syncInitialState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
    
    // Clean up if modal was open
    if (this.isOpen) {
      this.#handleClose();
    }
  }

  #syncInitialState() {
    // Ensure modal starts in closed state
    this.classList.add('hidden');
    this.setAttribute('aria-hidden', 'true');
  }

  #addEventListeners() {
    // Listen for global modal events with this modal's ID
    ThemeBus.addEventListener(EVENTS.modalOpen, this.#handleGlobalOpen);
    ThemeBus.addEventListener(EVENTS.modalClose, this.#handleGlobalClose);
    ThemeBus.addEventListener(EVENTS.modalToggle, this.#handleGlobalToggle);

    // Trigger buttons
    this.triggerButtons.forEach((btn) => {
      btn.addEventListener('click', this.#handleToggle);
    });

    // Close buttons inside modal
    this.closeButtons.forEach((btn) => {
      btn.addEventListener('click', this.#handleClose);
    });

    // Close on overlay click (but not on dialog click)
    if (this.overlay) {
      this.overlay.addEventListener('click', this.#handleOverlayClick);
    }

    // Prevent clicks inside dialog from closing modal
    if (this.dialog) {
      this.dialog.addEventListener('click', this.#stopPropagation);
    }

    // Close on Escape key
    document.addEventListener('keydown', this.#handleEscapeKey);
  }

  #removeEventListeners() {
    ThemeBus.removeEventListener(EVENTS.modalOpen, this.#handleGlobalOpen);
    ThemeBus.removeEventListener(EVENTS.modalClose, this.#handleGlobalClose);
    ThemeBus.removeEventListener(EVENTS.modalToggle, this.#handleGlobalToggle);

    this.triggerButtons.forEach((btn) => {
      btn.removeEventListener('click', this.#handleToggle);
    });

    this.closeButtons.forEach((btn) => {
      btn.removeEventListener('click', this.#handleClose);
    });

    if (this.overlay) {
      this.overlay.removeEventListener('click', this.#handleOverlayClick);
    }

    if (this.dialog) {
      this.dialog.removeEventListener('click', this.#stopPropagation);
    }

    document.removeEventListener('keydown', this.#handleEscapeKey);
  }

  #handleGlobalOpen = (event) => {
    // Only respond to events for this specific modal
    if (event.detail?.id === this.modalId) {
      this.#handleOpen();
    }
  };

  #handleGlobalClose = (event) => {
    // Only respond to events for this specific modal
    if (event.detail?.id === this.modalId) {
      this.#handleClose();
    }
  };

  #handleGlobalToggle = (event) => {
    // Only respond to events for this specific modal
    if (event.detail?.id === this.modalId) {
      this.#handleToggle();
    }
  };

  #handleOpen = () => {
    if (this.isOpen) return;

    this.isOpen = true;
    
    // Show modal
    this.classList.remove('hidden');
    this.setAttribute('aria-hidden', 'false');

    // Prevent body scroll
    document.body.classList.add('overflow-hidden');

    // Show drawer overlay via ThemeBus
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayShow));

    // Focus first focusable element in modal
    this.#focusFirstElement();
  };

  #handleClose = () => {
    if (!this.isOpen) return;

    this.isOpen = false;

    // Hide modal
    this.classList.add('hidden');
    this.setAttribute('aria-hidden', 'true');

    // Remove body scroll lock
    document.body.classList.remove('overflow-hidden');

    // Hide drawer overlay via ThemeBus
    ThemeBus.dispatchEvent(new Event(EVENTS.drawerOverlayHide));
  };

  #handleToggle = () => {
    this.isOpen ? this.#handleClose() : this.#handleOpen();
  };

  #handleOverlayClick = () => {
    // Only close if this modal is open
    if (this.isOpen) {
      this.#handleClose();
    }
  };

  #handleEscapeKey = (event) => {
    // Close on Escape key if this modal is open
    if (event.key === 'Escape' && this.isOpen) {
      this.#handleClose();
    }
  };

  #stopPropagation = (event) => {
    // Prevent clicks inside dialog from closing modal
    event.stopPropagation();
  };

  #focusFirstElement() {
    // Focus first focusable element in modal for accessibility
    const focusableElements = this.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }
}

// Initialize all modals when DOM is ready
function initModals() {
  if (!customElements.get('modal-component')) {
    customElements.define('modal-component', ModalComponent);
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initModals);
} else {
  initModals();
}

export { ModalComponent, initModals };

